import { phone as parsePhone } from "phone";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { formatDate, formatDateUtc, formatTime } from "#app/lib/utils/format-date";
import {
  countryCodes,
  currencyFractionDigits,
  currencyLocale,
  currencySymbol,
  getDefaultValueByType,
  numberDecimalPlaces,
  numberThousandsSeparator,
  phoneExampleByIso,
  isDayString,
  type CountryCode,
  type DateKind,
} from "@salestrak/custom-fields";

export type { CustomFieldDef };

/** The `AmountInput` shape a money field's currency config resolves to. Every
 *  money editor (cell, drawer, filter row, automation sentence) reads it here,
 *  so the symbol, separators and decimal cap cannot disagree across them. */
export function moneyInputProps(currency: string | null | undefined): {
  symbol: string;
  locale: string;
  fractionDigits: number;
} {
  return {
    symbol: currencySymbol(currency),
    locale: currencyLocale(currency),
    fractionDigits: currencyFractionDigits(currency),
  };
}

/** The `AmountInput` shape a number field's config resolves to: no symbol,
 *  the viewer's locale, the field's thousands-separator choice, and its
 *  decimal places as a ROUNDING cap — extra decimals are accepted while
 *  typing and rounded on commit, the opposite of money's hard minor-unit
 *  rule. */
export function numberInputProps(config: CustomFieldDef["config"]): {
  fractionDigits: number;
  fractionOverflow: "round";
  grouping: boolean;
} {
  return {
    fractionDigits: numberDecimalPlaces(config),
    fractionOverflow: "round",
    grouping: numberThousandsSeparator(config),
  };
}

/** A numeric value as any layer holds it — the stored number, the form
 *  layer's decimal string, or blank — as the number the editor takes. */
export function toNumericValue(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Sentinel select option for "clear" / None. Renderers inject it at the top of
 *  single and multi select dropdowns; choosing it maps to `null` or `[]`. It is
 *  a display value, so it never reaches a schema or the API. */
export const NONE_OPTION_VALUE = "__none__";

/** The em dash every "no value" reads as — the None option row, and the empty
 *  marker a cell, drawer property or placeholder shows in place of a value.
 *  One character for the concept: a guard that asks "is this the empty
 *  marker?" compares against this, never against a literal. */
export const NONE_OPTION_LABEL = "—";

/** Visual chrome for a custom-field control's primary input. `"table"` is the
 *  borderless inline-cell look; `"default"` the filled form-field look;
 *  `"ghost"` the bordered transparent look (automation condition builder);
 *  `"outline"` the page-fill bordered look with a hover border-brighten (the
 *  custom-field create/edit drawer, whose panel is background-filled). */
export type FieldSurface = "table" | "default" | "ghost" | "outline";

/** Control height for a custom-field input — the library's control scale. */
export type FieldControlSize = "xs" | "sm" | "default" | "lg" | "xl";

/**
 * Pixel heights for the control scale. The library ships no numeric
 * control-height map (its Input sizes are Tailwind height classes with their
 * own `sm:` step), so the values live here — the one consumer is the composite
 * field shell below, which needs a number for `minHeight`.
 */
const FIELD_CONTROL_HEIGHT_PX: Record<FieldControlSize, number> = {
  xs: 24,
  sm: 28,
  default: 32,
  lg: 40,
  xl: 48,
};

/**
 * Shared shell chrome (Box props) for composite field renderers — the
 * money/phone/url rows and the address field. "default", "ghost" and
 * "outline" get a bordered shell; only "default" fills it ("outline" shows
 * the page fill through and brightens its border on hover, matching the
 * library controls' outline variant). `size` sets the control-height
 * minimum so shells align with sibling controls (`lg` = 40px).
 *
 * These are `@salestrak/ui` Box props, so the fill is the `muted` token —
 * there is no `input-subtle` background.
 */
export function compositeShellProps(surface: FieldSurface, size?: FieldControlSize) {
  const bordered = surface !== "table";
  return {
    minHeight: size ? FIELD_CONTROL_HEIGHT_PX[size] : undefined,
    border: bordered ? (true as const) : undefined,
    borderColor: bordered ? ("input" as const) : undefined,
    rounded: bordered ? ("md" as const) : undefined,
    background: surface === "default" ? ("muted" as const) : undefined,
    className:
      surface === "outline"
        ? "transition-[border-color] not-focus-within:hover:border-muted-foreground/60"
        : undefined,
  };
}

export type { CountryCode };

export const typedCountryCodes: readonly CountryCode[] = countryCodes;

export const countryCodeMap = typedCountryCodes.reduce<Record<string, CountryCode>>(
  (map, c) => {
    map[c.country] = c;
    return map;
  },
  {},
);

export function countryFlag(iso: string): string {
  return [...iso.toUpperCase()]
    .map((ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65))
    .join("");
}

/** Validate a phone number against its selected country. Empty input is
 *  considered valid. Mobile-prefix checking stays off — it rejects real
 *  landlines, which a CRM must accept. */
export function validatePhoneNumber(phoneText: string, countryKey: string): boolean {
  if (!phoneText.trim()) return true;
  const country = countryCodeMap[countryKey];
  const fullNumber = phoneText.startsWith("+") ? phoneText : `${country?.code ?? "+1"}${phoneText}`;
  return parsePhone(fullNumber, { validateMobilePrefix: false }).isValid;
}

/** Reverse-lookup: dial code → first matching ISO country code. */
export const dialCodeToIso = typedCountryCodes.reduce<Record<string, string>>(
  (map, c) => {
    if (!map[c.code]) map[c.code] = c.country;
    return map;
  },
  {},
);

/** Extract the 2-letter country ISO from a phone value object. */
export function getPhoneCountryIso(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const obj = value as { country?: string; countryCode?: string };
  if (obj.country) return obj.country;
  if (obj.countryCode) return dialCodeToIso[obj.countryCode] ?? null;
  return null;
}

/** Placeholder hint for the phone input: the selected country's formatted
 *  example number from the package's generated table. The generic fallback
 *  covers a country the table lacks (a dissolved territory). */
export function getExamplePhoneForCountry(iso: string): string {
  return phoneExampleByIso[iso] ?? "Phone number";
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Validate email format. Empty input is considered valid. */
export function validateEmail(email: string): boolean {
  if (!email.trim()) return true;
  return EMAIL_REGEX.test(email);
}

export const KEYBOARD_TYPE_MAP: Record<string, string> = {
  text: "default",
  number: "numeric",
  email: "email-address",
};

/** The display text of a date-family value, per the field's kind
 *  (`dateKindOf`). A stored calendar day ("YYYY-MM-DD") names its day
 *  outright, whatever the kind — it has no time to show. An instant reads
 *  its local day for the calendar-day kind, day + time for the instant
 *  kind, and the clock alone for a time of day. */
export function formatDateValue(value: unknown, kind: DateKind | null): string {
  if (value == null) return "";
  if (isDayString(value)) {
    // `new Date("YYYY-MM-DD")` is UTC midnight; read it back in UTC so the
    // named day survives every zone.
    return formatDateUtc(new Date(value).toISOString());
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (isNaN(date.getTime())) return String(value);
  const iso = date.toISOString();
  switch (kind) {
    case "time":
      return formatTime(iso);
    case "dateTime":
      return `${formatDate(iso)} ${formatTime(iso)}`;
    default:
      return formatDate(iso);
  }
}

export function toDateValue(value: unknown): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(String(value));
  return isNaN(d.getTime()) ? null : d;
}

// Cardinality for member/photos custom fields. The `multiple` config flag is
// optional; an absent flag means MULTI — matching the API contract (see
// custom-field.object.ts "Absent means true") and the field-create form
// default. Single mode is only when `multiple` is explicitly false. Use this
// everywhere instead of inline `=== true` / truthy checks, which disagree on
// the absent case. (The option types do NOT consult this — select is single
// BY TYPE and labels is the multi twin, the reference single/multi split;
// stale `multiple` rows on old select fields are ignored.)
export function isMultipleField(
  config?: { multiple?: boolean | null } | null,
): boolean {
  return config?.multiple !== false;
}

// Initial value for a custom field on a NEW record: the field's authored
// default when present and valid, otherwise the type's empty default.
// Applied only at record creation — editing a field's default never
// rewrites existing records.
export function getInitialCustomFieldValue(field: CustomFieldDef): unknown {
  const configured = field.config?.defaultValue;
  if (configured != null) {
    // Guard a select/labels default (array of option values) against options
    // that may have since changed.
    if (field.type === "select" || field.type === "labels") {
      // A select default may arrive as a scalar from legacy/imported data;
      // normalize to the array shape the unified select type expects rather
      // than dropping it, then prune values no longer backed by an option.
      const arr = Array.isArray(configured) ? configured : [configured];
      const valid = new Set(
        (field.config?.options ?? []).map((o) => o?.value ?? o?.label ?? ""),
      );
      return arr.filter((v) => valid.has(v as string));
    } else {
      return configured;
    }
  }
  // Resolve by the field's own type — getDefaultValue() would route through
  // getQuestionType(field, {}) and, with an empty map, return the wrong
  // ('' string) default for array types like select/member.
  return getDefaultValueByType(field.type);
}

/**
 * Index a page of custom fields by `customFieldId`. The rows travel as they
 * arrive — every renderer reads the same `CustomFieldDefinition` the query
 * selected, so there is nothing to re-shape (R8).
 */
export function buildCustomFieldDefMap(
  fields: readonly CustomFieldDef[],
): Record<string, CustomFieldDef> {
  const map: Record<string, CustomFieldDef> = {};
  for (const cf of fields) map[cf.customFieldId] = cf;
  return map;
}
